import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { DataContext } from './context';
import { fetchInitial, fetchNavigation, fetchPage, fetchSite } from './services';
import { DataCollection, PageData } from './types';

export const DataProvider = ({ children }: { children: ReactNode }) => {
	const isInitial = useRef(true);
	const locale = useRef<string | null>(null);
	const pages = useRef<Record<string, PageData>>({});
	const [data, setData] = useState<Partial<DataCollection>>({});

	const loadPage = useCallback(async (pathname: string, search: string) => {
		const href = `${pathname}${search}`;
		if (isInitial.current) {
			isInitial.current = false;
			const data = await fetchInitial(href);
			locale.current = data.site.locale;
			pages.current[href] = data.overlayPage || data.page || data.backgroundPage;
			setData({
				...data,
				page: data.page || data.backgroundPage,
			});
			return data;
		}

		if (!pages.current[href]) {
			const data = await fetchPage(href);
			pages.current[href] = data;
		}

		const newData: Partial<DataCollection> = {
			overlayPage: undefined,
			backgroundPage: undefined,
			[pages.current[href].isOverlay ? 'overlayPage' : 'page']: pages.current[href],
		};

		if (locale.current !== pages.current[href].locale) {
			newData.site = await fetchSite(pages.current[href].locale + search);
			newData.navigation = await fetchNavigation(pages.current[href].locale + search);
			locale.current = pages.current[href].locale;
		}

		let result;
		setData((oldData) => {
			result = {
				...oldData,
				...newData,
			};
			return result;
		});
		return result;
	}, []);

	const context = useMemo(() => ({ loadPage, data }), [loadPage, data]);
	return <DataContext.Provider value={context}>{children}</DataContext.Provider>;
};
